<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import type { NyxAnswer, NyxClient } from '@atum-remote/nyx-link-protocol';
import {
  activitySummary,
  clientShortLabel,
  type ToolStatus,
  type TranscriptItem,
} from '../lib/nyx-presentation.js';
import AppIcon from './AppIcon.vue';
import NyxApprovalCard from './NyxApprovalCard.vue';
import NyxQuestionCard from './NyxQuestionCard.vue';

/**
 * The conversation itself. It follows new output while the person is reading
 * the end, and offers a way back down once they have scrolled up.
 */
const props = defineProps<{
  items: TranscriptItem[];
  taskId: string;
  client: NyxClient;
  running: boolean;
  truncated: boolean;
  canDecide: boolean;
  disabled: boolean;
}>();
const emit = defineEmits<{
  decide: [requestId: string, approve: boolean];
  answer: [requestId: string, answers: NyxAnswer[]];
}>();
const scroller = ref<HTMLElement>();
const pinned = ref(true);
const unseen = ref(false);
const statusLabel: Readonly<Record<ToolStatus, string>> = {
  pending: 'in progress',
  done: 'finished',
  failed: 'failed',
};

function onScroll() {
  const el = scroller.value;
  if (!el) return;
  pinned.value = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  if (pinned.value) unseen.value = false;
}
function scrollToBottom() {
  const el = scroller.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
  pinned.value = true;
  unseen.value = false;
}
function scrollToPending() {
  const target = scroller.value?.querySelector<HTMLElement>('[data-pending="true"]');
  if (!target) return;
  target.scrollIntoView({ block: 'center' });
  target.focus?.();
}
watch(
  () => [props.items.length, props.taskId, props.running] as const,
  async ([count, id], [previousCount, previousId]) => {
    await nextTick();
    if (id !== previousId || pinned.value) scrollToBottom();
    else if (count > (previousCount ?? 0)) unseen.value = true;
  },
  { flush: 'post' },
);
defineExpose({ scrollToPending, scrollToBottom });
</script>

<template>
  <div class="nyx-transcript-wrap">
    <div
      ref="scroller"
      class="nyx-transcript"
      role="log"
      aria-live="polite"
      aria-label="Conversation"
      @scroll.passive="onScroll"
    >
      <p v-if="truncated" class="nyx-transcript-note">
        <AppIcon name="clock" />Earlier events are outside the live history window.
      </p>
      <p v-if="!items.length && !running" class="nyx-transcript-empty">
        Nothing has happened in this task yet.
      </p>
      <template v-for="item in items" :key="item.key">
        <article v-if="item.kind === 'message'" class="nyx-message" :data-speaker="item.speaker">
          <span class="nyx-speaker">{{
            item.speaker === 'user' ? 'You' : clientShortLabel[client]
          }}</span>
          <div class="nyx-bubble">{{ item.text }}</div>
        </article>
        <details v-else-if="item.kind === 'activity'" class="nyx-activity">
          <summary>
            <AppIcon name="terminal" />
            <span class="nyx-activity-title">{{ activitySummary(item.tools) }}</span>
            <span
              v-if="item.tools.some((tool) => tool.status === 'pending') && running"
              class="nyx-activity-spinner"
              aria-hidden="true"
            ></span>
            <AppIcon name="chevronDown" class="nyx-activity-chevron" />
          </summary>
          <ul class="nyx-tools">
            <li
              v-for="tool in item.tools"
              :key="tool.key"
              class="nyx-tool"
              :data-status="tool.status"
            >
              <div class="nyx-tool-head">
                <i class="nyx-tool-dot" aria-hidden="true" />
                <code class="nyx-tool-name">{{ tool.name }}</code>
                <span class="nyx-tool-state">{{ statusLabel[tool.status] }}</span>
              </div>
              <pre v-if="tool.detail" class="nyx-code">{{ tool.detail }}</pre>
              <pre v-if="tool.result" class="nyx-code nyx-tool-result">{{ tool.result }}</pre>
            </li>
          </ul>
        </details>
        <NyxApprovalCard
          v-else-if="item.kind === 'approval'"
          :action="item.action"
          :summary="item.summary"
          :pending="item.pending"
          :decision="item.decision"
          :can-decide="canDecide"
          :disabled="disabled"
          @decide="(approve) => emit('decide', item.requestId, approve)"
        />
        <NyxQuestionCard
          v-else-if="item.kind === 'question'"
          :questions="item.questions"
          :pending="item.pending"
          :answered="item.answered"
          :can-decide="canDecide"
          :disabled="disabled || !canDecide"
          @answer="(answers) => emit('answer', item.requestId, answers)"
        />
        <p v-else class="nyx-notice" :data-tone="item.tone">
          <AppIcon :name="item.tone === 'danger' ? 'warning' : 'info'" />{{ item.text }}
        </p>
      </template>
      <div v-if="running" class="nyx-working" role="status">
        <span class="nyx-working-dots" aria-hidden="true"><i /><i /><i /></span>
        {{ clientShortLabel[client] }} is working
      </div>
    </div>
    <button v-if="!pinned" type="button" class="nyx-jump small" @click="scrollToBottom">
      <AppIcon name="chevronDown" />{{ unseen ? 'New activity' : 'Jump to latest' }}
    </button>
  </div>
</template>
